import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useCommandState } from 'cmdk';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { useRegisterCommands, useRegisterPage, useRegisterShortcut, usePluginNavigation } from '../hooks';
import { CommandList } from '../components/CommandList';
import { Footer } from '../components/CommandFooter';
import { CommandAction } from '../components/CommandShortcuts';
import { useFullRelease } from '@/common/hooks_useFullRelease';
import { getPathToEntity, normalizeText, type GraphNode } from '@packages/commons';
import { useParsedGraph } from '@/pages/models/release/GraphContext';
import { getNodeType } from '@/common';
import { trimDocName } from '@/hooks/documents';
import type { Command as CommandType } from '../types';
import { AttributePreview, useEditAttributeModal } from '@/pages/models/release/DataModel/route/Attributes/AttributeEditModal';
import styles from '../CommandPalette.module.scss';
import { AttributeUsage } from '@/pages/models/release/DataModel/route/Attributes/DocumentUsage';
import { AttributeIDField } from '@/pages/models/release/DataModel/route/Attributes/AttributeIDField';
import { cn } from '@/lib/utils';

const PLUGIN_ID = 'graph-plugin';
const GRAPH_PAGE = 'graph';

const severityFunctionMap: Record<string, string> = {
  error: 'RaiseError',
  warning: 'RaiseWarning',
  info: 'isInfo',
};

const buildValidationRule = (node: any, rawRelationships: any[]) => {
  if (node.nodeType !== 'validation') return null;
  const severity = severityFunctionMap[node.validation.severity] ?? node.validation.severity;
  const entity = node.entity && node.entity !== 'global' ? rawRelationships.find((rel) => rel.name === node.entity) : null;
  return `${severity}("${node.validation.message}"${entity ? `, ${entity.textSingular}` : ''})`;
};

const buildEntityPath = (entity: string | undefined, rawRelationships: any[]): string | null => {
  if (!entity) return null;
  if (entity === 'global') return 'global';
  const path = getPathToEntity(entity, rawRelationships);
  return path.length > 0 ? path.join(' / ') : null;
};

// ---------------------------------------------------------------------------
// useGraphCommands — builds all commands from graph nodes and relationships
// ---------------------------------------------------------------------------

const useGraphCommands = (): CommandType[] => {
  const { close } = useCommandPaletteContext();
  const editAttributeModal = useEditAttributeModal();
  const release = useFullRelease();
  const graph = useParsedGraph();

  const handleSelect = useCallback((node: any) => {
    editAttributeModal(node.id);
    close();
  }, [editAttributeModal, close]);

  return useMemo(() => {
    if (!release || !graph) return [];

    const rawRelationships = release.relationships ?? [];
    const nodeCommands: CommandType[] = [];
    const triggerCommands: CommandType[] = [];

    // Single iteration through all graph nodes
    for (const id of graph.ids()) {
      // Don't include fk in the search. These are actually important, but confuse our rule authors. At some point I'll be asked to turn these back on :)
      if (graph.field(id, "fk")) continue;
      const node = graph.node(id) as GraphNode;

      const fullDocPathNoExt: string = trimDocName(node.definedIn || '');
      const documentName = fullDocPathNoExt.substring(fullDocPathNoExt.lastIndexOf('/') + 1);
      const documentPath = fullDocPathNoExt.substring(0, fullDocPathNoExt.lastIndexOf('/'));
      // const isInput = !node.definedIn || node.definedIn === '';
      const nodeType = getNodeType(node);
      const isInput = nodeType === 'input';

      const processedNode = {
        v: node.id,
        ...node,
        documentName: documentName || '',
        documentPath: documentPath || '/',
        isInput,
        nodeType,
      };

      const baseKeywords = [
        node.publicId, node.text, node.name, node.label,
        node.id, documentName, documentPath, nodeType,
      ].filter(Boolean) as string[];

      const isValidation = nodeType === 'validation';
      const validationRule = buildValidationRule(processedNode, rawRelationships);
      const validationKeywords = isValidation
        ? ['validation', node.validation?.severity, validationRule].filter(Boolean) as string[]
        : [];

      nodeCommands.push({
        id: `graph-node-${node.id}`,
        label: String(validationRule || node.description || node.publicId || node.text || node.name || node.label || node.id),
        description: buildEntityPath(node.entity, rawRelationships) ?? '',
        category: isInput ? 'Inputs' : 'Graph Nodes',
        keywords: ['attribute', ...baseKeywords, ...validationKeywords],
        primary: {
          id: `graph-node-${node.id}-select`,
          label: 'Select',
          action: () => handleSelect(processedNode),
        },
        data: processedNode,
      });

      // Triggers become their own commands so they are always searchable
      if (Array.isArray(node.triggers) && node.triggers.length > 0) {
        triggerCommands.push({
          id: `graph-trigger-${node.id}`,
          label: String(node.description || node.publicId || node.text || node.name || node.label || node.id),
          description: buildEntityPath(node.entity, rawRelationships) ?? '',
          category: 'Triggers',
          keywords: ['trigger', ...baseKeywords],
          primary: {
            id: `graph-trigger-${node.id}-select`,
            label: 'Select',
            action: () => handleSelect(processedNode),
          },
          data: processedNode,
        });
      }
    }

    // Relationships
    const relationshipCommands: CommandType[] = [];
    for (const relationship of rawRelationships) {
      const texts = [
        { text: relationship.text, type: 'text' },
        { text: relationship.textSingular, type: 'textSingular' },
      ].filter((item) => Boolean(item.text));

      for (const { text, type } of texts) {
        if (!text) continue;
        const relData = { ...relationship, id: `${relationship.id}:${type}`, nodeType: 'relationship' as const };
        relationshipCommands.push({
          id: `graph-relationship-${relationship.id}:${type}`,
          label: normalizeText(text),
          description: '',
          category: 'Relationships',
          keywords: ['relationship', relationship.id],
          primary: {
            id: `graph-relationship-${relationship.id}:${type}-select`,
            label: 'Select',
            action: () => handleSelect(relData),
          },
          data: relData,
        });
      }
    }

    return [...nodeCommands, ...triggerCommands, ...relationshipCommands];
  }, [graph, release, handleSelect]);
};

// ---------------------------------------------------------------------------
// GraphPage — rendered when the palette navigates to GRAPH_PAGE
// ---------------------------------------------------------------------------

/**
 * GraphPage renders the filterable graph node / relationship list.
 * Filtering is handled by CommandList via the system scoring pipeline.
 */
const GraphPage: React.FC<{ commands: CommandType[] }> = ({ commands }) => {
  const { close } = useCommandPaletteContext();
  const [previewOpen, setPreviewOpen] = useState(false);

  // Track the currently highlighted item via cmdk's internal state.
  // state.value is the `value` prop of the focused Command.Item, which we set
  // to command.id in CommandItem. Must be used inside a <Command> element.
  const highlightedValue = useCommandState((state) => state.value);

  // Intercept Tab at the document level while this page is mounted.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        setPreviewOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Set a data attribute on the cmdk dialog root so CSS can widen it when
  // the preview panel is open, then clean up on unmount.
  useEffect(() => {
    const dialogEl = document.querySelector('[cmdk-root]') as HTMLElement | null;
    if (dialogEl) {
      dialogEl.setAttribute('data-preview', previewOpen ? 'true' : 'false');
    }
    return () => {
      const el = document.querySelector('[cmdk-root]') as HTMLElement | null;
      el?.removeAttribute('data-preview');
    };
  }, [previewOpen]);

  const highlighted = useMemo(() => {
    const command = commands.find((cmd) => cmd.id === highlightedValue) ?? null;
    const node = command?.data ?? null;
    return { command, node };
  }, [commands, highlightedValue]);

  return (
    <div className={styles.graphPageWrapper}>
      <div className={styles.graphPageBody}>
        <CommandList commands={commands} delay={300} emptyMessage="No graph nodes found." />
        <div className={styles.graphPreviewPanel} data-open={previewOpen}>
          <div className={cn(styles.graphPreviewContent, 'scrollable')}>
            {highlighted.node ? (
              <>
                <AttributePreview node={highlighted.node} />
                {/* we want to display the value for ease of dev/debugging */}
                <AttributeIDField id={highlighted.node.id} />
                <AttributeUsage showInterviews node={highlighted.node} onClick={close} />
              </>
            ) : (
              <p className={styles.graphPreviewEmpty}>Select an item to preview</p>
            )}
          </div>
        </div>
      </div>
      <Footer>
        <CommandAction
          text="Toggle Preview"
          shortcutKey="Tab"
          onClick={() => setPreviewOpen((prev) => !prev)}
        />
      </Footer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// GraphPlugin — registers global commands + graph page
// ---------------------------------------------------------------------------

/**
 * GraphPlugin - Provides graph node search and navigation
 *
 * Global commands (shown in the main palette):
 *   - "Search Graph" → opens the graph page
 *
 * Pages:
 *   - graph: GraphPage — filterable list of nodes and relationships
 */
export const GraphPlugin: React.FC = () => {
  const { navigateTo, openAndNavigate } = usePluginNavigation(PLUGIN_ID);
  const commands = useGraphCommands();

  const globalCommands = useMemo((): CommandType[] => [
    {
      id: 'open-graph-search',
      label: 'Search Rules',
      description: 'Search through attributes and relationships',
      category: 'Graph',
      shortcut: 'mod+shift+k',
      keywords: ['graph', 'search', 'nodes', 'relationships', 'rules'],
      primary: {
        id: 'open-graph-search-action',
        label: 'Open Graph Search',
        action: () => navigateTo(GRAPH_PAGE, "Search for an attribute or relationship..."),
      },
    },
  ], [navigateTo]);

  useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: GRAPH_PAGE, component: () => <GraphPage commands={commands} /> });

  useRegisterShortcut({
    shortcut: 'mod+shift+k',
    label: 'Search Graph',
    handler: () => openAndNavigate(GRAPH_PAGE, "Search for an attribute or relationship..."),
  });

  return null;
};
